// Edición manual de un disco (Req. 2.6). Reutiliza los campos y reglas del alta
// (vinylFields): mismos desplegables de catálogo (género/subgénero), validación
// de obligatorios con bordes rojos, y cover picker con las imágenes del release
// de Discogs. Todos los campos del vinilo son editables. Al guardar, el backend
// resuelve nombres→ids (findOrCreate) y valida el estado final.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../api/ApiContext.js';
import { useAsync } from '../ui/useAsync.js';
import { AlertMessage, AsyncBoundary, ButtonSpinner, Icon } from '../ui/components.js';
import type { Record as VinylRecord } from '../types.js';
import {
  ConditionSelect,
  CoverPicker,
  CustomFieldsSection,
  DateField,
  EditField,
  FieldLabel,
  FormSection,
  GenreSubgenreFields,
  SelectField,
  YesNoField,
  missingCustomKeys,
  numStr,
  toNum,
  useRecordFieldConfig,
} from './vinylFields.js';
import { RecordImageGallery } from './RecordImageGallery.js';

/** Estado editable: todos los campos del vinilo (nombres, no ids). */
interface EditState {
  artist: string | null;
  title: string | null;
  genre_propio: string | null;
  subgenre: string | null;
  discogs_genre: string | null;
  discogs_styles: string | null;
  edition_year: number | null;
  original_release_year: number | null;
  label: string | null;
  label_catalog_nr: string | null;
  origin_country: string | null;
  type: string | null;
  size_inches: number | null;
  discs_per_album: number | null;
  copies: number | null;
  media_condition: string | null;
  sleeve_condition: string | null;
  vinyl_color: string | null;
  grams: number | null;
  has_sleeve: boolean | null;
  original_cover: boolean | null;
  last_cleaned: string | null;
  discogs_format: string | null;
  barcode: string | null;
  commercial_value: number | null;
  comments: string | null;
  discogs_notes: string | null;
  cover_image_url: string | null;
}

type RequiredKey =
  | 'artist' | 'title' | 'genre_propio' | 'subgenre' | 'label' | 'label_catalog_nr'
  | 'edition_year' | 'origin_country' | 'type' | 'size_inches' | 'discs_per_album'
  | 'copies' | 'media_condition' | 'sleeve_condition';

/**
 * Campos obligatorios que FALTAN, acotados al conjunto obligatorio del tenant
 * (`requiredKeys`). Solo se marca un campo si está vacío Y el tenant lo exige
 * (Req. campos configurables): la obligatoriedad es dinámica por cuenta.
 */
function missingRequiredKeys(s: EditState, requiredKeys: Set<string>): Set<RequiredKey> {
  const hasText = (v: string | null | undefined) => typeof v === 'string' && v.trim() !== '';
  const hasNum = (v: number | null | undefined) => typeof v === 'number' && Number.isFinite(v) && v > 0;
  const req = (k: RequiredKey) => requiredKeys.has(k);
  const missing = new Set<RequiredKey>();
  if (req('artist') && !hasText(s.artist)) missing.add('artist');
  if (req('title') && !hasText(s.title)) missing.add('title');
  if (req('genre_propio') && !hasText(s.genre_propio)) missing.add('genre_propio');
  if (req('subgenre') && !hasText(s.subgenre)) missing.add('subgenre');
  if (req('label') && !hasText(s.label)) missing.add('label');
  if (req('label_catalog_nr') && !hasText(s.label_catalog_nr)) missing.add('label_catalog_nr');
  if (req('edition_year') && !hasNum(s.edition_year)) missing.add('edition_year');
  if (req('origin_country') && !hasText(s.origin_country)) missing.add('origin_country');
  if (req('type') && !hasText(s.type)) missing.add('type');
  if (req('size_inches') && !hasNum(s.size_inches)) missing.add('size_inches');
  if (req('discs_per_album') && !hasNum(s.discs_per_album)) missing.add('discs_per_album');
  if (req('copies') && !hasNum(s.copies ?? 1)) missing.add('copies');
  if (req('media_condition') && !s.media_condition) missing.add('media_condition');
  if (req('sleeve_condition') && !s.sleeve_condition) missing.add('sleeve_condition');
  return missing;
}

/** Mapea el disco cargado a estado editable (usa los NOMBRES resueltos). */
function toEditState(r: VinylRecord): EditState {
  return {
    artist: r.artist_name ?? null,
    title: r.title ?? null,
    genre_propio: r.genre_name ?? null,
    subgenre: r.subgenre ?? null,
    discogs_genre: r.discogs_genre ?? null,
    discogs_styles: r.discogs_styles ?? null,
    edition_year: r.edition_year ?? null,
    original_release_year: r.original_release_year ?? null,
    label: r.label_name ?? null,
    label_catalog_nr: r.label_catalog_nr ?? null,
    origin_country: r.origin_country ?? null,
    type: r.type ?? null,
    size_inches: r.size_inches ?? null,
    discs_per_album: r.discs_per_album ?? null,
    copies: r.copies ?? null,
    media_condition: r.media_condition ?? null,
    sleeve_condition: r.sleeve_condition ?? null,
    vinyl_color: r.vinyl_color ?? null,
    grams: r.grams ?? null,
    has_sleeve: r.has_sleeve ?? null,
    original_cover: r.original_cover ?? null,
    last_cleaned: r.last_cleaned ?? null,
    discogs_format: r.discogs_format ?? null,
    barcode: r.barcode ?? null,
    commercial_value: r.commercial_value ?? null,
    comments: r.comments ?? null,
    discogs_notes: r.discogs_notes ?? null,
    // La carátula elegida arranca en null: solo se envía si el usuario cambia.
    cover_image_url: null,
  };
}

export function EditVinylView() {
  const { id = '' } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const record = useAsync<VinylRecord>(() => api.records.get(id), [id]);

  return (
    <AsyncBoundary state={record}>
      {(r) => <EditForm record={r} onDone={() => navigate(`/records/${id}`, { replace: true })} />}
    </AsyncBoundary>
  );
}

function EditForm({ record: r, onDone }: { record: VinylRecord; onDone: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const fieldConfig = useRecordFieldConfig('edit');
  const [s, setS] = useState<EditState>(() => toEditState(r));
  const [custom, setCustom] = useState<Record<string, unknown>>(
    () => ({ ...((r.custom_fields as Record<string, unknown> | null) ?? {}) }),
  );
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discogsImages, setDiscogsImages] = useState<string[]>([]);
  // Eliminación del disco (Req.: eliminar de la colección dejando historial con
  // comentario). El comentario es obligatorio; el modal lo recoge.
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteComment, setDeleteComment] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  // Obligatoriedad dinámica por cuenta: un campo es requerido solo si el tenant
  // lo marcó así. `req(k)` controla tanto el asterisco como la validación.
  const req = (k: RequiredKey) => fieldConfig.requiredFrontKeys.has(k);

  // Imágenes del release de Discogs para elegir carátula (si tiene release).
  useEffect(() => {
    let cancelled = false;
    api.records
      .discogsImages(r.id)
      .then((res) => {
        if (!cancelled) setDiscogsImages(res.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setDiscogsImages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api, r.id]);

  const missing = useMemo(
    () => missingRequiredKeys(s, fieldConfig.requiredFrontKeys),
    [s, fieldConfig.requiredFrontKeys],
  );
  const missingCustom = useMemo(
    () => new Set(missingCustomKeys(fieldConfig.customFields, custom)),
    [fieldConfig.customFields, custom],
  );
  const invalid = (k: RequiredKey) => showErrors && missing.has(k);

  function set<K extends keyof EditState>(key: K, value: EditState[K]) {
    setS((prev) => ({ ...prev, [key]: value }));
  }

  function setCustomValue(key: string, value: unknown) {
    setCustom((prev) => ({ ...prev, [key]: value }));
  }

  // Galería de opciones de carátula: la actual (si existe) + las de Discogs, sin
  // duplicados. La actual va primero.
  const coverOptions = useMemo(() => {
    const urls: string[] = [];
    if (r.cover_image_url) urls.push(r.cover_image_url);
    for (const u of discogsImages) if (!urls.includes(u)) urls.push(u);
    return urls;
  }, [r.cover_image_url, discogsImages]);

  async function save() {
    if (missing.size > 0 || missingCustom.size > 0) {
      setShowErrors(true);
      const standardLabels = [...missing].map((k) => t(`recordForm.requiredLabels.${k}`));
      const customLabels = fieldConfig.customFields
        .filter((f) => missingCustom.has(f.key))
        .map((f) => f.label);
      setError(t('recordForm.missingRequired', { fields: [...standardLabels, ...customLabels].join(', ') }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.records.update(r.id, {
        title: s.title ?? '',
        artist_name: s.artist,
        label_name: s.label,
        genre_propio: s.genre_propio,
        subgenre: s.subgenre,
        discogs_genre: s.discogs_genre,
        discogs_styles: s.discogs_styles,
        edition_year: s.edition_year,
        original_release_year: s.original_release_year,
        label_catalog_nr: s.label_catalog_nr,
        origin_country: s.origin_country,
        type: s.type,
        size_inches: s.size_inches,
        discs_per_album: s.discs_per_album ?? 1,
        copies: s.copies ?? 1,
        media_condition: s.media_condition,
        sleeve_condition: s.sleeve_condition,
        vinyl_color: s.vinyl_color,
        grams: s.grams,
        has_sleeve: s.has_sleeve,
        original_cover: s.original_cover,
        last_cleaned: s.last_cleaned,
        discogs_format: s.discogs_format,
        barcode: s.barcode,
        commercial_value: s.commercial_value,
        comments: s.comments,
        discogs_notes: s.discogs_notes,
        // Campos personalizados del tenant (Req. campos configurables): se envían
        // siempre para que el backend persista/actualice sus valores.
        custom_fields: custom,
        // Solo se envía la carátula si el usuario eligió una distinta.
        ...(s.cover_image_url ? { cover_image_url: s.cover_image_url } : {}),
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('editVinyl.errSave'));
    } finally {
      setBusy(false);
    }
  }

  // Enfoca el campo de comentario al abrir el modal de eliminación.
  useEffect(() => {
    if (showDelete) commentRef.current?.focus();
  }, [showDelete]);

  async function confirmDelete() {
    const comment = deleteComment.trim();
    if (comment === '') {
      setDeleteError(t('editVinyl.errCommentRequired'));
      commentRef.current?.focus();
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.records.remove(r.id, comment);
      // Vuelve al catálogo (el disco ya no existe; no tiene sentido su ficha).
      navigate('/', { replace: true });
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : t('editVinyl.errDelete'));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="flex flex-col">
      {/* Barra superior: cancelar / guardar */}
      <div className="sticky top-0 z-20 px-margin-mobile py-space-sm bg-surface-base/80 backdrop-blur border-b border-border-subtle flex items-center justify-between gap-space-sm">
        <button
          type="button"
          onClick={onDone}
          className="flex items-center gap-space-xs text-on-surface-variant font-body-md text-body-md"
        >
          <Icon name="close" className="text-[20px]" /> {t('editVinyl.cancel')}
        </button>
        <button type="button" className="btn-primary !h-10 !px-space-md" disabled={busy} onClick={() => void save()}>
          {busy ? <ButtonSpinner /> : <Icon name="save" className="text-[18px]" />} {busy ? t('editVinyl.saving') : t('editVinyl.save')}
        </button>
      </div>

      <div className="px-margin-mobile pt-space-md">
        <h1 className="font-headline-lg-mobile text-headline-lg-mobile font-bold text-on-surface">{t('editVinyl.pageTitle')}</h1>
        <p className="font-body-sm text-body-sm text-on-surface-variant mt-space-xs">
          {t('editVinyl.pageHint')}
        </p>
      </div>

      <AlertMessage message={error} className="mx-margin-mobile mt-space-sm" />

      <div className="px-margin-mobile pb-space-lg flex flex-col gap-space-sm">
        {/* Identidad */}
        <FormSection title={t('recordForm.sections.identity')}>
          <div className="grid grid-cols-1 gap-space-sm">
            <EditField label={t('recordForm.artist')} required={req('artist')} invalid={invalid('artist')} value={s.artist} onChange={(v) => set('artist', v || null)} />
            <EditField label={t('recordForm.title')} required={req('title')} invalid={invalid('title')} value={s.title} onChange={(v) => set('title', v || null)} />
          </div>
        </FormSection>

        {/* Clasificación */}
        <FormSection title={t('recordForm.sections.classification')}>
          <div className="grid grid-cols-1 gap-space-sm">
            <GenreSubgenreFields
              genre={s.genre_propio}
              subgenre={s.subgenre}
              onGenreChange={(v) => set('genre_propio', v || null)}
              onSubgenreChange={(v) => set('subgenre', v)}
              genreInvalid={invalid('genre_propio')}
              subgenreInvalid={invalid('subgenre')}
              genreRequired={req('genre_propio')}
              subgenreRequired={req('subgenre')}
            />
            <EditField label={t('recordForm.discogsGenre')} value={s.discogs_genre} onChange={(v) => set('discogs_genre', v || null)} />
            <EditField label={t('recordForm.discogsStyles')} value={s.discogs_styles} onChange={(v) => set('discogs_styles', v || null)} />
          </div>
        </FormSection>

        {/* Edición / sello */}
        <FormSection title={t('recordForm.sections.editionLabel')}>
          <div className="grid grid-cols-2 gap-space-sm">
            <EditField label={t('recordForm.editionYear')} required={req('edition_year')} invalid={invalid('edition_year')} value={numStr(s.edition_year)} onChange={(v) => set('edition_year', toNum(v))} />
            <EditField label={t('recordForm.originalReleaseYear')} value={numStr(s.original_release_year)} onChange={(v) => set('original_release_year', toNum(v))} />
            <EditField label={t('recordForm.label')} required={req('label')} invalid={invalid('label')} value={s.label} onChange={(v) => set('label', v || null)} />
            <EditField label={t('recordForm.catalogNr')} required={req('label_catalog_nr')} invalid={invalid('label_catalog_nr')} value={s.label_catalog_nr} onChange={(v) => set('label_catalog_nr', v || null)} />
            <EditField label={t('recordForm.originCountry')} required={req('origin_country')} invalid={invalid('origin_country')} value={s.origin_country} onChange={(v) => set('origin_country', v || null)} />
            <EditField label={t('recordForm.barcode')} value={s.barcode} onChange={(v) => set('barcode', v || null)} />
          </div>
        </FormSection>

        {/* Formato / físico */}
        <FormSection title={t('recordForm.sections.formatPhysical')}>
          <div className="grid grid-cols-2 gap-space-sm">
            <SelectField label={t('recordForm.type')} required={req('type')} invalid={invalid('type')} value={s.type} options={['Album', 'Single', 'Soundtrack', 'EP', 'Other']} onChange={(v) => set('type', v)} />
            <SelectField label={t('recordForm.sizeInches')} required={req('size_inches')} invalid={invalid('size_inches')} value={s.size_inches != null ? String(s.size_inches) : null} options={['12', '10', '7']} onChange={(v) => set('size_inches', toNum(v ?? ''))} />
            <EditField label={t('recordForm.discsPerAlbum')} required={req('discs_per_album')} invalid={invalid('discs_per_album')} value={numStr(s.discs_per_album)} onChange={(v) => set('discs_per_album', toNum(v))} />
            <EditField label={t('recordForm.copies')} required={req('copies')} invalid={invalid('copies')} value={numStr(s.copies ?? 1)} onChange={(v) => set('copies', toNum(v))} />
            <EditField label={t('recordForm.vinylColor')} value={s.vinyl_color} onChange={(v) => set('vinyl_color', v || null)} />
            <EditField label={t('recordForm.grams')} value={numStr(s.grams)} onChange={(v) => set('grams', toNum(v))} />
            <EditField label={t('recordForm.discogsFormat')} value={s.discogs_format} onChange={(v) => set('discogs_format', v || null)} />
          </div>
        </FormSection>

        {/* Estado físico */}
        <FormSection title={t('recordForm.sections.physicalCondition')}>
          <div className="grid grid-cols-2 gap-space-sm">
            <ConditionSelect label={t('recordForm.mediaCondition')} required={req('media_condition')} invalid={invalid('media_condition')} value={s.media_condition} onChange={(v) => set('media_condition', v)} />
            <ConditionSelect label={t('recordForm.sleeveCondition')} required={req('sleeve_condition')} invalid={invalid('sleeve_condition')} value={s.sleeve_condition} onChange={(v) => set('sleeve_condition', v)} />
            <YesNoField label={t('recordForm.hasSleeve')} value={s.has_sleeve} onChange={(v) => set('has_sleeve', v)} />
            <YesNoField label={t('recordForm.originalCover')} value={s.original_cover} onChange={(v) => set('original_cover', v)} />
            <DateField label={t('recordForm.lastCleaned')} value={s.last_cleaned} onChange={(v) => set('last_cleaned', v || null)} />
          </div>
        </FormSection>

        {/* Valor y comentarios */}
        <FormSection title={t('recordForm.sections.valueComments')}>
          <div className="grid grid-cols-1 gap-space-sm">
            <EditField label={t('recordForm.commercialValue')} value={numStr(s.commercial_value)} onChange={(v) => set('commercial_value', toNum(v))} />
            <div className="flex flex-col gap-space-xs">
              <FieldLabel label={t('recordForm.personalComments')} />
              <textarea
                className="w-full min-h-[96px] rounded-lg bg-surface-deck border border-border-subtle focus:border-border-focus focus:outline-none transition-colors px-space-sm py-space-xs text-on-surface font-body-md text-body-md placeholder:text-outline resize-y"
                value={s.comments ?? ''}
                onChange={(e) => set('comments', e.target.value || null)}
              />
            </div>
            <div className="flex flex-col gap-space-xs">
              <FieldLabel label={t('recordForm.discogsNotes')} />
              <textarea
                className="w-full min-h-[96px] rounded-lg bg-surface-deck border border-border-subtle focus:border-border-focus focus:outline-none transition-colors px-space-sm py-space-xs text-on-surface font-body-md text-body-md placeholder:text-outline resize-y"
                value={s.discogs_notes ?? ''}
                onChange={(e) => set('discogs_notes', e.target.value || null)}
              />
            </div>
          </div>
        </FormSection>

        {/* Campos personalizados del tenant (Req. campos configurables) */}
        <CustomFieldsSection
          defs={fieldConfig.customFields}
          values={custom}
          onChange={setCustomValue}
          invalidKeys={showErrors ? missingCustom : undefined}
        />

        {/* Carátula */}
        <FormSection title={t('recordForm.sections.cover')}>
          {/* Imágenes de Discogs (si el disco tiene release asociado): elegir una
              como carátula al guardar. */}
          {coverOptions.length > 0 && (
            <>
              <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-xs">
                {t('editVinyl.coverFromDiscogs')}
              </p>
              <CoverPicker
                images={coverOptions}
                selected={s.cover_image_url ?? r.cover_image_url ?? null}
                onSelect={(url) => set('cover_image_url', url)}
              />
            </>
          )}

          {/* Imágenes PROPIAS del disco: subir foto (o tomar con la cámara),
              marcar principal, reordenar y borrar. Se guardan en S3 al instante
              (no dependen del botón Guardar). */}
          <div className={coverOptions.length > 0 ? 'mt-space-md' : ''}>
            <p className="section-label text-vacuum-glow mb-space-xs">{t('editVinyl.yourPhotos')}</p>
            <RecordImageGallery recordId={r.id} primaryUrl={r.cover_image_url ?? null} />
          </div>
        </FormSection>

        {/* Zona de peligro: eliminar el disco de la colección (Req.: historial de
            eliminación con comentario). */}
        <FormSection title={t('editVinyl.deleteSection')}>
          <div className="flex flex-col gap-space-sm rounded-lg border border-error/40 bg-error/5 p-space-sm">
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              {t('editVinyl.deleteHint')}
            </p>
            <button
              type="button"
              onClick={() => {
                setDeleteComment('');
                setDeleteError(null);
                setShowDelete(true);
              }}
              className="flex items-center justify-center gap-space-xs h-10 px-space-md rounded-lg bg-error text-on-error font-body-md text-body-md font-bold self-start"
            >
              <Icon name="delete" className="text-[18px]" /> {t('editVinyl.deleteButton')}
            </button>
          </div>
        </FormSection>
      </div>

      {/* Modal de confirmación de eliminación con comentario obligatorio. */}
      {showDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-surface-base/70 backdrop-blur-sm px-margin-mobile"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-record-title"
        >
          <div className="w-full max-w-md rounded-xl bg-surface-deck border border-border-subtle p-space-md flex flex-col gap-space-sm">
            <div className="flex items-center gap-space-xs text-error">
              <Icon name="warning" className="text-[22px]" />
              <h2 id="delete-record-title" className="font-headline-sm text-headline-sm font-bold">
                {t('editVinyl.deleteTitle')}
              </h2>
            </div>
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              {t('editVinyl.deleteConfirmLead', {
                record: r.title ?? t('editVinyl.deleteThisRecord'),
                artist: r.artist_name ? t('editVinyl.deleteOfArtist', { artist: r.artist_name }) : '',
              })}
            </p>

            <div className="flex flex-col gap-space-xs">
              <FieldLabel label={t('editVinyl.deleteCommentLabel')} />
              <textarea
                ref={commentRef}
                className="w-full min-h-[88px] rounded-lg bg-surface-base border border-border-subtle focus:border-border-focus focus:outline-none transition-colors px-space-sm py-space-xs text-on-surface font-body-md text-body-md placeholder:text-outline resize-y"
                placeholder={t('editVinyl.deleteCommentPlaceholder')}
                value={deleteComment}
                onChange={(e) => setDeleteComment(e.target.value)}
                disabled={deleting}
              />
            </div>

            <AlertMessage message={deleteError} />

            <div className="flex items-center justify-end gap-space-sm mt-space-xs">
              <button
                type="button"
                onClick={() => setShowDelete(false)}
                disabled={deleting}
                className="h-10 px-space-md rounded-lg border border-border-subtle text-on-surface-variant font-body-md text-body-md"
              >
                {t('editVinyl.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void confirmDelete()}
                disabled={deleting || deleteComment.trim() === ''}
                className="flex items-center gap-space-xs h-10 px-space-md rounded-lg bg-error text-on-error font-body-md text-body-md font-bold disabled:opacity-50"
              >
                {deleting ? <ButtonSpinner /> : <Icon name="delete" className="text-[18px]" />}
                {deleting ? t('editVinyl.deleting') : t('editVinyl.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
